import type { CSSProperties } from 'react';
import type { ImageMediaSize } from '@/blocks/domain/types';
import type { TemplateLogoAlignment } from '@/templates/domain/types/TemplateLogo';
import { LOGO_SIZE_HEIGHT_PX, LOGO_SIZE_MAX_WIDTH_PX, logoPdfObjectPosition } from './logoLayout';

/**
 * Image element boxes (CSS px). The three boxes are the logo boxes scaled up, because a picture needs more room than a mark; `'full'`
 * is the whole width of the column, at the picture's own proportions. The HTML preview and the PDF both read these, so a picture is
 * as large in the printed quote as it is in the editor.
 */
const IMAGE_BOX_SCALE = 2.5;

/**
 * The tallest a full-width picture grows (CSS px, 720pt): the content height of a Letter page with the standard margins. A taller
 * picture (a phone screenshot, a long strip) is drawn whole inside that height instead of overflowing the page or being squeezed.
 */
const FULL_IMAGE_MAX_HEIGHT_PX = 960;

/** The largest box of a fixed size, or `null` for `'full'` (the column's width decides). */
export function imageBoxPx(size: ImageMediaSize): { maxWidth: number; maxHeight: number } | null {
  if (size === 'full') return null;
  return { maxWidth: LOGO_SIZE_MAX_WIDTH_PX[size] * IMAGE_BOX_SCALE, maxHeight: LOGO_SIZE_HEIGHT_PX[size] * IMAGE_BOX_SCALE };
}

/** HTML preview style of an Image element's `<img>`. */
export function imageHtmlStyle(size: ImageMediaSize): CSSProperties {
  const box = imageBoxPx(size);
  if (!box) return { width: '100%', height: 'auto', maxHeight: FULL_IMAGE_MAX_HEIGHT_PX, objectFit: 'contain', display: 'block' };
  return { maxWidth: box.maxWidth, maxHeight: box.maxHeight, objectFit: 'contain', display: 'block' };
}

/** react-pdf style of an Image element's `<Image>`: the same box in points, or the full column width at the picture's proportions. */
export function imagePdfStyle(
  size: ImageMediaSize,
  pxToPt: (px: number) => number,
  alignment: TemplateLogoAlignment
):
  | { width: '100%'; maxHeight: number; objectFit: 'contain' }
  | { maxWidth: number; maxHeight: number; objectFit: 'contain'; objectPosition: string } {
  const box = imageBoxPx(size);
  if (!box) return { width: '100%', maxHeight: pxToPt(FULL_IMAGE_MAX_HEIGHT_PX), objectFit: 'contain' };
  return {
    maxWidth: pxToPt(box.maxWidth),
    maxHeight: pxToPt(box.maxHeight),
    objectFit: 'contain',
    objectPosition: logoPdfObjectPosition(alignment)
  };
}
